/**
 * A tiny scoped pub/sub so components OUTSIDE the ProcessEditor subtree — the Diagram
 * right panel's "Go to Parameters" links — can switch the editor's active tab. Lifting
 * the active tab into the shared screen-design model would drag a pure-UI concern into
 * the Top-risk schema; a module-local emitter keeps it decoupled and typed. Only one
 * ProcessEditor is mounted at a time, but the listener set tolerates more.
 */

type ProcessEditorTabListener = (tab: string) => void;

const listeners: Set<ProcessEditorTabListener> = new Set();

/** ProcessEditor subscribes; returns an unsubscribe for cleanup. */
export const subscribeProcessEditorTab = (listener: ProcessEditorTabListener): (() => void) => {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
};

// Retained ONLY when no ProcessEditor is mounted: a cross-tool caller (the screen lists' workflow
// shortcuts fire right after switching the dashboard to the flows tool) requests the tab before the
// editor mounts. A mounted editor handles the request live and nothing is retained — otherwise a
// long-stale tab would fire on the next unrelated process open.
let pendingTab: string | undefined;

/** Any component can ask the open ProcessEditor to switch tabs (retained until one mounts). */
export const requestProcessEditorTab = (tab: string): void => {
  if (listeners.size === 0) {
    pendingTab = tab;
    return;
  }
  listeners.forEach((listener: ProcessEditorTabListener): void => listener(tab));
};

/** Read and clear a retained tab request — ProcessEditor calls this on mount. */
export const consumePendingProcessEditorTab = (): string | undefined => {
  const tab: string | undefined = pendingTab;
  pendingTab = undefined;
  return tab;
};

// --- Active-tab state channel ---------------------------------------------------------
// The reverse direction: ProcessEditor publishes whichever tab is active, and observers
// (the diagram right panel) read it so they can hide themselves off the Diagram tab.

let activeTab: string | undefined;
const activeTabListeners: Set<ProcessEditorTabListener> = new Set();

/** ProcessEditor publishes its active tab (on mount and on every change). */
export const publishActiveProcessEditorTab = (tab: string): void => {
  activeTab = tab;
  activeTabListeners.forEach((listener: ProcessEditorTabListener): void => listener(tab));
};

/** The last published active tab, or undefined when no ProcessEditor is mounted. */
export const getActiveProcessEditorTab = (): string | undefined => activeTab;

/** Observe the active tab; returns an unsubscribe for cleanup. */
export const subscribeActiveProcessEditorTab = (listener: ProcessEditorTabListener): (() => void) => {
  activeTabListeners.add(listener);
  return (): void => {
    activeTabListeners.delete(listener);
  };
};
